import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Box,
  Chip,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import MappedInSpaceSelector from "./MappedInSpaceSelector";
import ToggleSwitch from "../backend/ToggleSwitch";
import CaptionUploadModal from "../CaptionUploadModal";
import { useCaptionUpload } from "../../hooks/useCaptionUpload";
import UseToast from "../UseToast";
import { useMappedInSettings } from "../../hooks/useMappedInSettings";
import { fetchStepsFromMappedIn } from "../../utils/mappedInSteps";
import { getLanguageText, setLanguageText } from "../../utils/multilanguageUtils";

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

// Helper function to check file type
const getFileType = (fileUrl) => {
  if (!fileUrl) return "blank";
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg"];
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];

  if (imageExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "image";
  }
  if (videoExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "video";
  }
  return "other";
};

// MediaDropzone Component
const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".mov", ".mp3"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: "2px dashed #cccccc",
        padding: "20px",
        borderRadius: "5px",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

// UploadButton Component
const UploadButton = ({ uploading, onUpload, buttonColor, ...props }) => (
  <button
    onClick={onUpload}
    type="button"
    style={{
      marginTop: "20px",
      backgroundColor: buttonColor,
      border: "none",
      borderRadius: "5px",
      cursor: "pointer",
      padding: "10px 20px",
      color: "#FFFFFF",
      ...props.style,
    }}
    disabled={uploading}
  >
    {uploading ? "Uploading..." : "Upload"}
  </button>
);

// UploadedUrlImage Component
const UploadedUrlImage = ({ url }) => {
  return (
    <>
      {url ? (
        <div style={{ margin: "auto" }}>
          {getFileType(url.fileUrl) === "image" ? (
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img
                src={url.fileUrl}
                style={{ maxWidth: "210px", maxHeight: "80px" }}
                alt=""
              />
            </a>
          ) : getFileType(url.fileUrl) === "video" ? (
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img
                src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
                style={{ maxWidth: "200px", maxHeight: "60px" }}
                alt="Play Video"
              />
            </a>
          ) : (
            ""
          )}
        </div>
      ) : null}
    </>
  );
};

// UploadedUrl Component
const UploadedUrl = ({ url, handleDelete }) =>
  url ? (
    <div
      style={{
        marginTop: "20px",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginTop: "15px",
        }}
      >
        <div>
          Uploaded File:{" "}
          <a
            href={url.fileUrl}
            style={{ fontWeight: "bold" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url.fileName}
          </a>
        </div>

        {url.fileUrl && handleDelete && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleDelete}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  ) : null;

const WayfinderLocationSettings = ({
  item,
  setItem,
  handleSave,
  buttonColor = "#000000",
  userId,
  locationId,
  mapVideoFile,
  setMapVideoFile,
  handleUploadMapVideo,
  avatarVideoFile,
  setAvatarVideoFile,
  handleUploadAvatarVideo,
  logoBeforeMapFile,
  setLogoBeforeMapFile,
  handleUploadLogoBeforeMap,
  currentLanguage = 'en',
}) => {


  // State for fetched categories and subcategories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Steps fetching state
  const [fetchingSteps, setFetchingSteps] = useState(false);
  const [stepsError, setStepsError] = useState(null);


  // Caption upload state
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const { handleCaptionUpload } = useCaptionUpload();
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  // Read mapped-in settings for this user (provides optional mappedInStartLocation values)
  const { settings: mappedInSettings } = useMappedInSettings(userId);

  // Tags state
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetch(`/api/tags/${userId}`);
        const data = await response.json();
        if (data.success) {
          setAvailableTags(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    if (userId) {
      fetchTags();
    }
  }, [userId]);

  // Load selected tags when item changes
  useEffect(() => {
    if (item?.tags && Array.isArray(item.tags)) {
      setSelectedTags(item.tags);
    } else {
      setSelectedTags([]);
    }
  }, [item?.tags]);

  // Handle tag selection change
  const handleTagsChange = (event, newValue) => {
    const tagIds = newValue.map(tag => tag._id);
    setSelectedTags(tagIds);
    setItem((prev) => ({
      ...prev,
      tags: tagIds,
    }));
  };



  // Handle basic field changes
  const handleFieldChange = (field, value) => {
    setItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle multilanguage name field change
  const handleNameChange = (value) => {
    setItem((prev) => {
      // Get the current name (could be string or object)
      const currentName = prev.name || {};

      // Update name for the current language
      const updatedName = setLanguageText(currentName, currentLanguage, value);

      // Also update buttonText to keep them synchronized
      const currentButtonText = prev.buttonText || {};
      const updatedButtonText = setLanguageText(currentButtonText, currentLanguage, value);

      return {
        ...prev,
        name: updatedName,
        buttonText: updatedButtonText,
      };
    });
  };

  const handleLogoBeforeMapToggle = (event) => {
    const enabled = event.target.checked;
    setItem((prev) => ({
      ...prev,
      logoBeforeMap: {
        ...prev.logoBeforeMap,
        enabled,
      },
    }));
  };

  // Handle MappedIn space selection
  const handleSpaceNameChange = (spaceName) => {
    handleFieldChange("spaceName", spaceName);
  };

  // Handle MappedIn floor level change
  const handleFloorLevelChange = (floorLevel) => {
    handleFieldChange("floorLevel", floorLevel);

  };

  // Handle MappedIn external ID change
  const handleExternalIdChange = (externalId) => {
    handleFieldChange("externalId", externalId);

    // Only auto-fetch if steps haven't been fetched before for this location
    // or if the external ID is different from what was previously fetched
    if (externalId && !item?.stepsFetched) {
      // fetchStepsForLocation(externalId);
    } else if (!externalId) {
      // Clear steps if no external ID
      clearStepsData();
    }
  };

  // Clear all steps data
  const clearStepsData = () => {
    handleFieldChange("totalSteps", 0);
    handleFieldChange("stepDetails", []);
    handleFieldChange("stepsFetched", false);
  };

  // Calculate step duration based on video duration
  const calculateStepDurations = (steps, totalSteps) => {
    if (!totalSteps || totalSteps === 0) return steps;

    let videoDurationSeconds = 0;


    // Priority 1: Custom avatar video duration (stored in seconds)
    if (item?.avatarVideo?.enabled && item?.avatarVideo?.videoDuration > 0) {
      videoDurationSeconds = item.avatarVideo.videoDuration;
    }
    // Priority 2: Generated multilanguage video duration (stored in milliseconds → convert to seconds)
    else if (item?.avatarVideosMultiLang?.[currentLanguage]?.videoDuration > 0) {
      videoDurationSeconds = Math.floor(item.avatarVideosMultiLang[currentLanguage].videoDuration / 1000);
    }
    // Priority 3: Default — 5 seconds per step
    else {
      videoDurationSeconds = totalSteps * 5;
    }

    // Get map delay time (default to 2 seconds if not set)
    const mapDelayTime = item?.mapDelayTime ?? 2;

    // Reserve mapDelayTime + 10 seconds buffer, but ensure at least 1 second per step
    const reservedTime = mapDelayTime + 10;
    const availableDuration = Math.max(videoDurationSeconds - reservedTime, totalSteps);

    // Distribute evenly across all steps (minimum 1 second each)
    const durationPerStep = Math.max(Math.floor(availableDuration / totalSteps), 1);


    return steps.map(step => ({
      ...step,
      duration: durationPerStep,
    }));
  };

  // Fetch steps from MappedIn
  const fetchStepsForLocation = async (externalId) => {
    if (!externalId) return;

    setFetchingSteps(true);
    setStepsError(null);

    try {
      const result = await fetchStepsFromMappedIn(externalId, item?.spaceName, userId);

      // Calculate step durations based on video duration
      const stepsWithDuration = calculateStepDurations(result.steps, result.totalSteps);

      // Update all step-related fields
      handleFieldChange("totalSteps", result.totalSteps);
      handleFieldChange("stepDetails", stepsWithDuration);
      handleFieldChange("stepsFetched", true);

      showMsgSuccessToast(`Successfully fetched ${result.totalSteps} steps from MappedIn with auto-calculated durations`);
    } catch (error) {
      console.error("Error fetching steps:", error);
      setStepsError(error.message);
      showErrorToast(`Please generate or Upload the video first to auto-fetch steps`);
    } finally {
      setFetchingSteps(false);
    }
  };

  // Manual re-fetch steps (force refresh)
  const refetchSteps = () => {
    if (item?.externalId) {
      fetchStepsForLocation(item.externalId);
    }
  };

  // Recalculate step durations based on current video duration
  const recalculateStepDurations = () => {
    if (!item?.stepDetails || item.stepDetails.length === 0) {
      showErrorToast("No steps available to recalculate durations");
      return;
    }

    const totalSteps = item.stepDetails.length;
    let videoDurationSeconds = 0;

    // Priority 1: Custom avatar video duration (seconds)
    if (item?.avatarVideo?.enabled && item?.avatarVideo?.videoDuration > 0) {
      videoDurationSeconds = item.avatarVideo.videoDuration;
    }
    // Priority 2: Generated multilanguage video duration (milliseconds → seconds)
    else if (item?.avatarVideosMultiLang?.[currentLanguage]?.videoDuration > 0) {
       videoDurationSeconds = Math.floor(item.avatarVideosMultiLang[currentLanguage].videoDuration / 1000);
    }
    // Priority 3: Default
    else {
     showErrorToast("Please create or upload the video first")
     return;
    }

    // Get map delay time (default to 2 seconds if not set)
    const mapDelayTime = item?.mapDelayTime ?? 2;

    // Reserve mapDelayTime + 10 seconds buffer, but ensure at least 1 second per step
    const reservedTime = mapDelayTime + 10;
    const availableDuration = Math.max(videoDurationSeconds - reservedTime, totalSteps);

    // Distribute evenly across all steps (minimum 1 second each)
    const durationPerStep = Math.max(Math.floor(availableDuration / totalSteps), 1);


    const updatedSteps = item.stepDetails.map(step => ({
      ...step,
      duration: durationPerStep,
    }));

    handleFieldChange("stepDetails", updatedSteps);
    showMsgSuccessToast(`Step durations recalculated: ${durationPerStep} seconds per step`);

  };

  // Handle step duration change
  const handleStepDurationChange = (stepIndex, duration) => {
    const currentStepDetails = item.stepDetails || [];
    const newStepDetails = [...currentStepDetails];

    if (newStepDetails[stepIndex]) {
      newStepDetails[stepIndex] = {
        ...newStepDetails[stepIndex],
        duration: parseFloat(duration) || 5
      };
      handleFieldChange("stepDetails", newStepDetails);
    }
  };

  // Get operation hours for a specific day (from array structure)
  const getDayHours = (day) => {
    if (!item.operationHours || !Array.isArray(item.operationHours)) {
      // Default hours if not set
      return {
        day,
        isOpen: true,
        isFullDay: false,
        open: day === "saturday" || day === "sunday" ? "10:00" : "09:00",
        close: day === "saturday" || day === "sunday" ? "16:00" : "17:00",
      };
    }

    const dayHours = item.operationHours.find((h) => h.day === day);

    if (!dayHours) {
      // Return default if not found
      return {
        day,
        isOpen: true,
        isFullDay: false,
        open: day === "saturday" || day === "sunday" ? "10:00" : "09:00",
        close: day === "saturday" || day === "sunday" ? "16:00" : "17:00",
      };
    }

    // Ensure we have all required fields with proper defaults
    return {
      ...dayHours,
      isOpen: dayHours.isOpen !== undefined ? dayHours.isOpen : true,
      isFullDay: dayHours.isFullDay !== undefined ? dayHours.isFullDay : false,
      open:
        dayHours.open ||
        (day === "saturday" || day === "sunday" ? "10:00" : "09:00"),
      close:
        dayHours.close ||
        (day === "saturday" || day === "sunday" ? "16:00" : "17:00"),
    };
  };

  // Handle operation hours change
  const handleOperationHoursChange = (day, field, value) => {
    setItem((prev) => {
      const currentHours = prev.operationHours || [];
      let dayHours = getDayHours(day);

      // Special handling for isFullDay
      if (field === "isFullDay") {
        if (value === true) {
          // When setting to 24 hours, set times to 00:00 and ensure open
          dayHours = {
            ...dayHours,
            [field]: true,
            isOpen: true,
            open: "00:00",
            close: "00:00",
          };
        } else {
          // When turning off 24 hours, restore previous times or defaults
          const hasSavedTimes = dayHours.open && dayHours.close;
          const previousOpen =
            dayHours.open ||
            (day === "saturday" || day === "sunday" ? "10:00" : "09:00");
          const previousClose =
            dayHours.close ||
            (day === "saturday" || day === "sunday" ? "16:00" : "17:00");

          dayHours = {
            ...dayHours,
            [field]: false,
            // Only set to defaults if we don't have saved times
            open: hasSavedTimes ? dayHours.open : previousOpen,
            close: hasSavedTimes ? dayHours.close : previousClose,
          };
        }
      } else if (field === "open" || field === "close") {
        // When time changes, update the value
        dayHours = {
          ...dayHours,
          [field]: value,
        };

        // If times are changed manually, turn off isFullDay and ensure open
        if (value !== "00:00") {
          dayHours.isFullDay = false;
        }

        // Ensure location is open when times are set
        if (value.trim() !== "") {
          dayHours.isOpen = true;
        }
      } else if (field === "isOpen") {
        // Toggle open/closed - preserve times!
        dayHours = {
          ...dayHours,
          [field]: value,
        };

        // When closing, don't clear times - just mark as closed
        // When opening, times should already be preserved
        if (!value) {
          dayHours.isFullDay = false;
        }
      } else {
        // For any other field
        dayHours = {
          ...dayHours,
          [field]: value,
        };
      }

      // Update the array
      const updatedHours = currentHours.filter((h) => h.day !== day);
      updatedHours.push(dayHours);

      // Ensure all days are present
      DAYS.forEach((dayObj) => {
        if (!updatedHours.find((h) => h.day === dayObj.key)) {
          updatedHours.push(getDayHours(dayObj.key));
        }
      });

      // Sort by day order
      updatedHours.sort((a, b) => {
        const dayOrder = DAYS.map((d) => d.key);
        return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      });

      return {
        ...prev,
        operationHours: updatedHours,
      };
    });
  };

  // Toggle location enabled/disabled
  const handleEnabledToggle = (event) => {
    const enabled = event.target.checked;
    setItem((prev) => ({
      ...prev,
      enabled,
      status: enabled,
    }));
  };

  // Get category name by ID
  const getCategoryName = (id) => {
    const category = categories.find((cat) => cat._id === id);
    return category ? category.name : id;
  };

  // Get subcategory name by ID
  const getSubcategoryName = (id) => {
    const subcategory = subcategories.find((sub) => sub._id === id);
    return subcategory ? subcategory.name : id;
  };

  // Helper functions for operation hours
  const setAllBusinessHours = () => {
    const updatedHours = DAYS.map((day) => ({
      day: day.key,
      isOpen: true,
      isFullDay: false,
      open: day.key === "saturday" || day.key === "sunday" ? "10:00" : "09:00",
      close: day.key === "saturday" || day.key === "sunday" ? "16:00" : "17:00",
    }));

    setItem((prev) => ({
      ...prev,
      operationHours: updatedHours,
    }));
  };

  const setAll24Hours = () => {
    const updatedHours = DAYS.map((day) => ({
      day: day.key,
      isOpen: true,
      isFullDay: true,
      open: "00:00",
      close: "00:00",
    }));

    setItem((prev) => ({
      ...prev,
      operationHours: updatedHours,
    }));
  };

  const setAllClosed = () => {
    setItem((prev) => {
      const currentHours = prev.operationHours || [];

      // Mark all as closed but preserve existing times
      const updatedHours = DAYS.map((day) => {
        const existingHours = currentHours.find((h) => h.day === day.key);
        const defaultHours = getDayHours(day.key);

        return {
          day: day.key,
          isOpen: false,
          isFullDay: false,
          // Preserve existing times or use defaults
          open: existingHours?.open || defaultHours.open,
          close: existingHours?.close || defaultHours.close,
        };
      });

      return {
        ...prev,
        operationHours: updatedHours,
      };
    });
  };

  const resetToDefaults = () => {
    const defaultHours = DAYS.map((day) => ({
      day: day.key,
      isOpen: true,
      isFullDay: false,
      open: day.key === "saturday" || day.key === "sunday" ? "10:00" : "09:00",
      close: day.key === "saturday" || day.key === "sunday" ? "16:00" : "17:00",
    }));

    setItem((prev) => ({
      ...prev,
      operationHours: defaultHours,
    }));
  };

  // Handle caption upload for custom avatar video
  const handleCustomAvatarCaptionUpload = async (
    captionFile,
    convertSrtToVtt,
  ) => {
    if (!captionFile) {
      // Delete caption
      setItem((prev) => ({
        ...prev,
        avatarVideo: {
          ...prev.avatarVideo,
          captionFile: "",
        },
      }));
      showMsgSuccessToast("Caption file removed successfully!");
      return;
    }

    await handleCaptionUpload(
      captionFile,
      convertSrtToVtt,
      (updateFn) => {
        setItem((prev) => {
          const updated = updateFn(prev);
          return {
            ...prev,
            avatarVideo: {
              ...prev.avatarVideo,
              captionFile: updated.avatarVideo?.captionFile || "",
            },
          };
        });
      },
      "avatarVideo",
      userId,
    );
  };

  return (
    <Box sx={{ mx: "auto", mt: 3 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        {/* Save Button */}{" "}
        <Box
          sx={{
            mt: 3,
            mb: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: buttonColor,
              "&:hover": { backgroundColor: buttonColor, opacity: 0.9 },
            }}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Location Settings"}
          </Button>
        </Box>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "#000000",
                    borderBottom: `2px solid #000000`,
                    pb: 1,
                  }}
                >
                  Basic Information
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <div className="form-group">
                    <label>Location Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={getLanguageText(item.name, currentLanguage) || ""}
                      onChange={(e) =>
                        handleNameChange(e.target.value)
                      }
                      placeholder="Enter location name"
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      value={item.description || ""}
                      onChange={(e) =>
                        handleFieldChange("description", e.target.value)
                      }
                      placeholder="Enter location description"
                      rows={3}
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Tags (for search)</label>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={availableTags}
                      getOptionLabel={(option) => getLanguageText(option.name, currentLanguage) || 'Unnamed Tag'}
                      value={availableTags.filter(tag => selectedTags.includes(tag._id))}
                      onChange={handleTagsChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select tags..."
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '4px',
                            },
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option._id}
                            label={getLanguageText(option.name, currentLanguage) || 'Unnamed Tag'}
                            sx={{
                              backgroundColor: option.color,
                              color: '#fff',
                              m: 0.5,
                            }}
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => (
                        <li
                          {...props}
                          style={{
                            ...props.style,
                            backgroundColor: selected ? `${option.color}20` : 'transparent',
                            fontWeight: selected ? 600 : 400,
                          }}
                        >
                          <Chip
                            label={getLanguageText(option.name, currentLanguage) || 'Unnamed Tag'}
                            size="small"
                            sx={{
                              backgroundColor: option.color,
                              color: '#fff',
                            }}
                          />
                        </li>
                      )}
                    />
                    <small className="form-text text-muted">
                      Tags help users find this location through search. Select relevant tags or create new ones in the Tag Manager.
                    </small>
                  </div>



                  {/* <div className="form-group mt-3">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item.enabled || false}
                          onChange={handleEnabledToggle}
                          color="primary"
                        />
                      }
                      label="Enable Location"
                      sx={{ color: buttonColor }}
                    />
                  </div> */}
                </Box>
              </CardContent>
            </Card>
          </Grid>{" "}
          {/* Parent Categories - Display Only */}
          <Grid item xs={12} md={6}>
            <div className="form-group mt-3">
              <MappedInSpaceSelector
                value={item.spaceName || ""}
                onChange={handleSpaceNameChange}
                onFloorChange={handleFloorLevelChange}
                onExternalIdChange={handleExternalIdChange}
                label="MappedIn Space"
                placeholder="Search and select a space from MappedIn..."
                buttonColor={buttonColor}
                userId={userId}
              />

              {/* Display selected floor level */}
              {item.floorLevel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Floor: {item.floorLevel}
                </Typography>
              )}
            </div>

            <div className="form-group mt-3">
              <label>Total Steps</label>
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <input
                  type="number"
                  className="form-control"
                  value={item.totalSteps || ""}
                  onChange={(e) =>
                    handleFieldChange("totalSteps", parseInt(e.target.value) || 0)
                  }
                  placeholder="Enter total number of steps"
                  min="0"
                  style={{ flex: 1 }}
                  disabled={fetchingSteps}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (item.externalId) {
                      refetchSteps();
                    } else {
                      alert('Please select a location from MappedIn Space selector first');
                    }
                  }}
                  disabled={!item.externalId || fetchingSteps}
                  sx={{
                    borderColor: buttonColor,
                    background: buttonColor,
                    color: 'white',
                    textTransform: "none",
                    fontWeight: "600",
                    minWidth: "120px",
                    "&:hover": {
                      borderColor: buttonColor,
                      color: '#181515',
                      backgroundColor: `${buttonColor}10`,
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  {fetchingSteps ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Fetching...
                    </>
                  ) : item?.stepsFetched ? (
                    "Re-fetch Steps"
                  ) : (
                    "Auto Fetch Steps"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (item.externalId) {
                      // Use dynamic map link and start location from settings
                      const baseUrl = mappedInSettings?.mapLink || 'https://web.mappedin.com/lga-laguardia/laguardia-airport.html';
                      const startLocation = mappedInSettings?.mappedInStartLocation || 'LOC-1IP30ZO0';
                      const base = baseUrl.split('#')[0].replace(/\/$/, '');
                      const url = `${base}#/directions?to=${item.externalId}&from=${startLocation}&textDirectionsVisible=true&instruction=0`;
                      window.open(url, '_blank');
                    } else {
                      alert('Please select a location from MappedIn Space selector first');
                    }
                  }}
                  disabled={!item.externalId}
                  sx={{
                    borderColor: buttonColor,
                    color: buttonColor,
                    textTransform: "none",
                    fontWeight: "600",
                    minWidth: "120px",
                    "&:hover": {
                      borderColor: buttonColor,
                      backgroundColor: `${buttonColor}10`,
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  View Directions
                </Button>
              </div>
              <small className="form-text text-muted">
                Total number of steps to reach this location.
                {item?.stepsFetched ? (
                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                    ✅ Steps fetched from MappedIn
                  </span>
                ) : (
                  "Use \"Auto Fetch Steps\" to automatically get steps from MappedIn."
                )}
              </small>

              {/* Debug Info - Remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Steps Info:</strong><br />
                  Steps Fetched: {item?.stepsFetched ? 'Yes' : 'No'}<br />
                  Total Steps: {item?.totalSteps || 0}<br />
                </Alert>
              )}

              {/* Error Display */}
              {stepsError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {stepsError}
                </Alert>
              )}
            </div>

            <div className="form-group mt-3">
              <label>Location Position ID for Pointer Map</label>
              <input
                type="text"
                className="form-control"
                value={item.locationPosId || ""}
                onChange={(e) =>
                  handleFieldChange("locationPosId", e.target.value)
                }
                placeholder="Enter location position ID for Pointr Map"
              />
              <small className="form-text text-muted">
                Position ID used for Pointr Map navigation (when Pointr Map is enabled in Map Settings).
              </small>
            </div>

            <div className="form-group mt-3">
              <label>Distance from Start (metres)</label>
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <input
                  type="number"
                  className="form-control"
                  value={item.distanceFromStart ?? ""}
                  onChange={(e) =>
                    handleFieldChange("distanceFromStart", e.target.value === "" ? null : parseFloat(e.target.value))
                  }
                  placeholder="e.g. 142"
                  min="0"
                  step="0.1"
                  style={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={async () => {
                    if (!item.externalId) {
                      showErrorToast('Please select a MappedIn space first');
                      return;
                    }
                    try {
                      showMsgSuccessToast('Fetching distance from MappedIn...');
                      const { getMappedInDirections } = await import('../../services/mappedInDirectionsService');

                      // Prefer mapped-in start location from settings if available
                      const startLocationId = mappedInSettings && mappedInSettings.mappedInStartLocation ? mappedInSettings.mappedInStartLocation : null;
                      const startLocationName = mappedInSettings && mappedInSettings.mappedInStartLocationName ? mappedInSettings.mappedInStartLocationName : null;

                      const result = await getMappedInDirections(item.externalId, userId, item.spaceName, startLocationId, startLocationName);
                      if (result.success && result.totalDistance != null) {
                        const distM = Math.round(result.totalDistance);
                        handleFieldChange("distanceFromStart", distM);
                        showMsgSuccessToast(`Distance fetched: ${distM} m`);
                      } else {
                        showErrorToast(result.error || 'Could not calculate distance');
                      }
                    } catch (err) {
                      showErrorToast('Failed to fetch distance: ' + err.message);
                    }
                  }}
                  disabled={!item.externalId}
                  sx={{
                    borderColor: buttonColor,
                    color: buttonColor,
                    textTransform: "none",
                    fontWeight: "600",
                    minWidth: "130px",
                    whiteSpace: "nowrap",
                    "&:hover": { borderColor: buttonColor, backgroundColor: `${buttonColor}10` },
                    "&:disabled": { borderColor: "#ccc", color: "#ccc" },
                  }}
                >
                  📏 Fetch Distance
                </Button>
              </div>
              <small className="form-text text-muted">
                Distance in metres from the kiosk/start location. Click "Fetch Distance" to auto-calculate from MappedIn.
              </small>
            </div>
          </Grid>

          {/* Step Duration Management */}
          {item.stepsFetched && item?.stepDetails && item?.stepDetails.length > 0 && (
            <Grid item xs={12} md={12}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: "#000000",
                      borderBottom: `2px solid #000000`,
                      pb: 1,
                    }}
                  >
                    Step Duration Management ({item.stepDetails.length} Steps)
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "#666", fontSize: "0.875rem" }}
                  >
                    Set the duration (in seconds) for each step of the directions. This helps control the timing of the navigation experience.
                    <br />
                    <strong>Auto-calculation:</strong> When steps are fetched, durations are automatically calculated by subtracting 10 seconds from the video duration and dividing evenly among all steps.
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {item.stepsFetched && item.stepDetails.map((step, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            sx={{
                              p: 2,
                              border: `1px solid #e0e0e0`,
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold", mb: 1, fontSize: '1rem' }}
                            >
                              Step {step.stepNumber}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                color: "#555",
                                fontSize: "1rem",
                                minHeight: "40px",
                                fontWeight: "600",
                                overflow: "auto",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical"
                              }}
                              title={step.text}
                            >
                              {step.text}
                            </Typography>

                            <div className="form-group">
                              <label style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                                Duration (seconds):
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={step.duration || 5}
                                onChange={(e) =>
                                  handleStepDurationChange(index, e.target.value)
                                }
                                min="0.1"
                                max="60"
                                step="0.1"
                                placeholder="5"
                                style={{
                                  marginTop: "5px",
                                  padding: "8px",
                                  fontSize: "0.875rem"
                                }}
                              />
                            </div>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Bulk Actions */}
                    <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const newStepDetails = item.stepDetails.map(step => ({
                            ...step,
                            duration: 5
                          }));
                          handleFieldChange("stepDetails", newStepDetails);
                        }}
                        sx={{ borderColor: buttonColor, color: '#1c1515' }}
                      >
                        Set All to 5 sec
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const newStepDetails = item.stepDetails.map(step => ({
                            ...step,
                            duration: 10
                          }));
                          handleFieldChange("stepDetails", newStepDetails);
                        }}
                        sx={{ borderColor: buttonColor, color: '#1c1515' }}
                      >
                        Set All to 10 sec
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const newStepDetails = item.stepDetails.map(step => ({
                            ...step,
                            duration: 3
                          }));
                          handleFieldChange("stepDetails", newStepDetails);
                        }}
                        sx={{ borderColor: buttonColor, color: '#1c1515' }}
                      >
                        Set All to 3 sec
                      </Button>

                      <Button
                        size="small"
                        variant="contained"
                        onClick={recalculateStepDurations}
                        sx={{
                          backgroundColor: buttonColor,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: buttonColor,
                            opacity: 0.9
                          }
                        }}
                      >
                        Auto-Calculate from Video Duration
                      </Button>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 2,
                        color: "#666",
                        fontStyle: "italic",
                        fontSize: "1rem"
                      }}
                    >
                      Total estimated time: {
                        (item.stepDetails || []).reduce((sum, step) => sum + (step.duration || 0), 0)
                      } seconds
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}



          {/* Map Delay Time - Outside the cards */}
          <Grid item xs={12} md={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "#000000",
                    borderBottom: `2px solid #000000`,
                    pb: 1,
                  }}
                >
                  Map Display Settings
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <div className="form-group" style={{ display: 'flex' }}>
                    <div className="col-md-6">
                      <label>Map Delay Time (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item?.mapDelayTime || 2}
                        onChange={(e) =>
                          handleFieldChange(
                            "mapDelayTime",
                            Math.max(0, parseInt(e.target.value) || 0),
                          )
                        }
                        min="0"
                        placeholder="2"

                      />
                      <small className="form-text text-muted">
                        Time in seconds before map appears after video starts
                      </small>
                    </div>

                    {/* <div className="col-md-6">
                      <label>Static Map Duration (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item?.staticMapDuration || 5}
                        onChange={(e) =>
                          handleFieldChange(
                            "staticMapDuration",
                            Math.max(1, parseInt(e.target.value) || 1),
                          )
                        }
                        min="1"
                        placeholder="5"

                      />
                      <small className="form-text text-muted">
                        Duration in seconds for which the static map should be displayed
                      </small>
                    </div> */}
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Run Only Avatar Video Toggle */}
          <Grid item xs={12} md={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "#000000",
                    borderBottom: `2px solid #000000`,
                    pb: 1,
                  }}
                >
                  QR Code & Video Mode Settings
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <div className="form-group">
                    <label>Enable QR Code:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={item?.enableQrCode !== false} // Default to true
                      id={"enableQrCode"}
                      onChange={(val) =>
                        handleFieldChange("enableQrCode", val)
                      }
                    />
                  </div>

                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 3, color: "#666", fontSize: "0.875rem" }}
                  >
                    When enabled, the global QR code will be displayed for this location.
                    QR code design is configured in Branding Settings.
                  </Typography>

                  <div className="form-group">
                    <label>Run Only Avatar Video:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={item?.runOnlyAvatarVideo}
                      id={"runOnlyAvatarVideo"}
                      onChange={(val) =>
                        handleFieldChange("runOnlyAvatarVideo", val)
                      }
                    />
                  </div>

                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "#666", fontSize: "0.875rem" }}
                  >
                    When enabled, only the avatar video will play without any
                    map functionality. This disables the MappedIn map component
                    and map video features.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Custom Avatar Video */}
          <Grid item xs={12} md={6}>
            <div className="col-md-12 d-flex p-3">
              <div
                className={`form-group menu-card2 p-3 ${!item?.avatarVideo?.enabled ? "disabled" : ""
                  } h-100 w-100`}
              >
                <div className="form-group">
                  <label>Custom Avatar Video:</label>

                  <ToggleSwitch
                    label="Enable"
                    isOn={item?.avatarVideo?.enabled || false}
                    id={"avatarVideoEnabled"}
                    onChange={(val) =>
                      handleFieldChange("avatarVideo", {
                        ...item?.avatarVideo,
                        enabled: val,
                      })
                    }
                  />
                </div>

                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "#666", fontSize: "0.875rem" }}
                >
                  Upload a custom avatar video to override generated multilanguage videos
                </Typography>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "5px",
                    marginTop: "15px",
                  }}
                >
                  <MediaDropzone
                    onDrop={(acceptedFiles) => {
                      if (acceptedFiles.length > 0 && setAvatarVideoFile) {
                        setAvatarVideoFile(acceptedFiles[0]);
                      }
                    }}
                    selectedFile={avatarVideoFile}
                  />

                  <UploadedUrlImage url={item?.avatarVideo} />
                </div>



                {/* Video Duration Input */}
                {item?.avatarVideo?.fileUrl && (
                  <div className="form-group mt-3">
                    <label>Video Duration (seconds)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={item?.avatarVideo?.videoDuration ?? 0}
                      onChange={(e) => {
                        const duration = parseInt(e.target.value) || 0;
                        handleFieldChange("avatarVideo", {
                          ...item.avatarVideo,
                          videoDuration: duration,
                        });
                      }}
                      min="0"
                      step="1"
                      placeholder="Enter duration in seconds"
                      style={{ maxWidth: "300px" }}
                    />
                    <small className="form-text text-muted">
                      Duration in seconds (e.g., 10 = 10 seconds).
                    </small>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    alignItems: "center",
                    marginTop: "20px",
                  }}
                >
                  <UploadButton
                    uploading={false}
                    onUpload={handleUploadAvatarVideo}
                    buttonColor={buttonColor}
                    style={{ margin: "0" }}
                  />
                  {/* {item?.avatarVideo?.captionFile && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "#28a745",
                        fontSize: "0.85rem",
                      }}
                    >
                      ✅ Caption file uploaded
                    </Typography>
                  )} */}

                  {item?.avatarVideo?.captionFile && "Manage Captions"}

                  <Button
                    variant="outlined"
                    onClick={() => setCaptionModalOpen(true)}
                    disabled={!item?.avatarVideo?.fileUrl}
                    sx={{
                      borderColor: buttonColor,
                      color: buttonColor,
                      textTransform: "none",
                      fontWeight: "600",
                      borderRadius: "5px",
                      padding: "10px 20px",
                      "&:hover": {
                        borderColor: buttonColor,
                        backgroundColor: `${buttonColor}10`,
                      },
                      "&:disabled": {
                        borderColor: "#ccc",
                        color: "#ccc",
                      },
                    }}
                  >
                    📝 Upload Caption
                  </Button>
                </div>
              </div>
            </div>
          </Grid>
          {/* Map Video */}
          <Grid item xs={12} md={6}>
            <div className="col-md-12 d-flex p-3">
              <div
                className={`form-group menu-card2 p-3 ${!item?.enableMapVideo ? "disabled" : ""} h-100 w-100`}
              >
                <div className="form-group">
                  <label>Map Video:</label>
                  <ToggleSwitch
                    label="Enable"
                    isOn={item?.enableMapVideo || false}
                    id={"enableMapVideo"}
                    onChange={(val) => handleFieldChange("enableMapVideo", val)}
                  />
                </div>

                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "#666", fontSize: "0.875rem" }}
                >
                  Upload a map video to show when no avatar video is available
                </Typography>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "5px",
                    marginTop: "15px",
                  }}
                >
                  <MediaDropzone
                    onDrop={(acceptedFiles) => {
                      if (acceptedFiles.length > 0 && setMapVideoFile) {
                        setMapVideoFile(acceptedFiles[0]);
                      }
                    }}
                    selectedFile={mapVideoFile}
                  />
                  <UploadedUrlImage url={item?.mapVideo} />
                </div>

                <div>
                  <UploadedUrl
                    url={item?.mapVideo}
                    handleDelete={() => {
                      handleFieldChange("mapVideo", {
                        fileUrl: "",
                        fileName: "",
                        enabled: false,
                        videoDuration: 0,
                      });
                      if (setMapVideoFile) setMapVideoFile(null);
                    }}
                  />
                </div>

                {/* Video Duration Input */}
                {item?.mapVideo?.fileUrl && (
                  <div className="form-group mt-3">
                    <label>Video Duration (seconds)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={item?.mapVideo?.videoDuration ?? 0}
                      onChange={(e) => {
                        const duration = parseInt(e.target.value) || 0;
                        handleFieldChange("mapVideo", {
                          ...item.mapVideo,
                          videoDuration: duration,
                        });
                      }}
                      min="0"
                      step="1"
                      placeholder="Enter duration in seconds"
                      style={{ maxWidth: "300px" }}
                    />
                    <small className="form-text text-muted">
                      Duration in seconds (e.g., 10 = 10 seconds).
                    </small>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: "10px",
                    alignItems: "center",
                    marginTop: "20px",
                  }}
                >
                  <UploadButton
                    uploading={false}
                    onUpload={handleUploadMapVideo}
                    buttonColor={buttonColor}
                    style={{ margin: "0" }}
                  />
                </div>
              </div>
            </div>
          </Grid>


          {/* <Grid item xs={12} md={6}>
            <div className="col-md-12 d-flex p-3">
              <div
                className={`form-group menu-card2 p-3 ${!item?.enableLogoBeforeMap ? "disabled" : ""} h-100 w-100`}
              >
                <div className="form-group">
                  <label>Logo Before Map:</label>
                  <ToggleSwitch
                    label="Enable"
                    isOn={item?.logoBeforeMap.enabled || false}
                    id={"logoBeforeMap"}
                    onChange={(val) => handleLogoBeforeMapToggle({ target: { checked: val } })}
                  />
                </div>

                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "#666", fontSize: "0.875rem" }}
                >
                  Upload a logo to display before the map loads
                </Typography>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "5px",
                    marginTop: "15px",
                  }}
                >
                  <MediaDropzone
                    onDrop={(acceptedFiles) => {
                      if (acceptedFiles.length > 0 && setLogoBeforeMapFile) {
                        setLogoBeforeMapFile(acceptedFiles[0]);
                      }
                    }}
                    selectedFile={logoBeforeMapFile}
                  />
                  <UploadedUrlImage url={item?.logoBeforeMap} />
                </div>

                <div>
                  <UploadedUrl
                    url={item?.logoBeforeMap}
                    handleDelete={() => {
                      handleFieldChange("logoBeforeMap", {
                        fileUrl: "",
                        fileName: "",
                        enabled: false,
                        width: 100,
                        displayDuration: 3,
                      });
                      if (setLogoBeforeMapFile) setLogoBeforeMapFile(null);
                    }}
                  />
                </div>



                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    alignItems: "center",
                    marginTop: "20px",
                  }}
                >
                  <UploadButton
                    uploading={false}
                    onUpload={handleUploadLogoBeforeMap}
                    buttonColor={buttonColor}
                    style={{ margin: "0" }}
                  />
 
                  {item?.logoBeforeMap?.fileUrl && (
                    <>
                      <div
                        className="dimensions"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "15px",
                          height: "100%",
                        }}
                      >
                       
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <label
                            style={{
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            Size (%):
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            style={{ width: "80px" }}
                            value={item?.logoBeforeMap?.width ?? 100}
                            onChange={(e) => {
                              const width = parseInt(e.target.value) || 100;
                              handleFieldChange("logoBeforeMap", {
                                ...item.logoBeforeMap,
                                width: width,
                              });
                            }}
                            min="0"
                            max="100"
                            placeholder="Size"
                          />
                        </div>

                   
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <label
                            style={{
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            Duration (s):
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            style={{ width: "80px" }}
                            value={item?.logoBeforeMap?.displayDuration ?? 3}
                            onChange={(e) => {
                              const duration = parseInt(e.target.value) || 3;
                              handleFieldChange("logoBeforeMap", {
                                ...item.logoBeforeMap,
                                displayDuration: Math.min(Math.max(duration, 0), 100),
                              });
                            }}
                            min="0"
                            placeholder="Duration"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Grid> */}


          <Grid item xs={12} md={12}>
            {/* Website URLs */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "#000000",
                    borderBottom: `2px solid #000000`,
                    pb: 1,
                  }}
                >
                  Website URLs
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <div className="form-group">
                    <label>Website URL</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item?.websiteUrl || ""}
                      onChange={(e) =>
                        handleFieldChange("websiteUrl", e.target.value)
                      }
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="form-group mt-3">
                    <label>Airport Website URL</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item?.airportUrl || ""}
                      onChange={(e) =>
                        handleFieldChange("airportUrl", e.target.value)
                      }
                      placeholder="https://airport.example.com"
                    />
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Operation Hours - Full Width */}
          <Grid item xs={12} md={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "#000000",
                    borderBottom: `2px solid #000000`,
                    pb: 1,
                  }}
                >
                  Operation Hours
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    mb: 3,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {/* <Button
                    size="small"
                    variant="outlined"
                    onClick={setAllBusinessHours}
                    sx={{ borderColor: buttonColor, color: buttonColor }}
                  >
                    Set All Business Hours (9-5)
                  </Button> */}

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={setAll24Hours}
                    sx={{ borderColor: buttonColor, color: "#000" }}
                  >
                    Set All 24 Hours
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={setAllClosed}
                    sx={{ borderColor: buttonColor, color: "#000" }}
                  >
                    Set All Closed
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={resetToDefaults}
                    sx={{ borderColor: buttonColor, color: "#000" }}
                  >
                    Reset to Defaults
                  </Button>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {DAYS.map((dayInfo) => {
                    const day = dayInfo.key;
                    const dayHours = getDayHours(day);

                    return (
                      <Grid item xs={12} sm={12} md={12} key={day}>
                        <Paper
                          sx={{
                            p: 2,
                            border: `1px solid ${!dayHours.isOpen
                              ? "#ff4444"
                              : dayHours.isFullDay
                                ? buttonColor
                                : "#e0e0e0"
                              }`,
                            backgroundColor: !dayHours.isOpen
                              ? "#fff5f5"
                              : dayHours.isFullDay
                                ? `${buttonColor}10`
                                : "transparent",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="medium">
                              {dayInfo.label}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={dayHours.isOpen !== false}
                                    onChange={(e) =>
                                      handleOperationHoursChange(
                                        day,
                                        "isOpen",
                                        e.target.checked,
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                                label={dayHours.isOpen ? "Open" : "Closed"}
                                labelPlacement="start"
                              />
                            </Box>
                          </Box>

                          {dayHours.isOpen && (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={dayHours.isFullDay || false}
                                      onChange={(e) =>
                                        handleOperationHoursChange(
                                          day,
                                          "isFullDay",
                                          e.target.checked,
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label="24 Hours"
                                  labelPlacement="start"
                                />
                              </Box>

                              {!dayHours.isFullDay && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 1,
                                  }}
                                >
                                  <input
                                    type="time"
                                    value={dayHours.open || ""}
                                    onChange={(e) =>
                                      handleOperationHoursChange(
                                        day,
                                        "open",
                                        e.target.value,
                                      )
                                    }
                                    className="form-control"
                                    style={{ padding: "6px", flex: 1 }}
                                    disabled={dayHours.isFullDay}
                                  />
                                  <Typography variant="body2">to</Typography>
                                  <input
                                    type="time"
                                    value={dayHours.close || ""}
                                    onChange={(e) =>
                                      handleOperationHoursChange(
                                        day,
                                        "close",
                                        e.target.value,
                                      )
                                    }
                                    className="form-control"
                                    style={{ padding: "6px", flex: 1 }}
                                    disabled={dayHours.isFullDay}
                                  />
                                </Box>
                              )}

                              {dayHours.isFullDay && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: buttonColor,
                                    fontStyle: "italic",
                                    mt: 1,
                                  }}
                                >
                                  24 Hours Open
                                </Typography>
                              )}
                            </>
                          )}

                          {!dayHours.isOpen && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#ff4444",
                                fontStyle: "italic",
                                mt: 1,
                              }}
                            >
                              Closed
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: buttonColor,
                "&:hover": { backgroundColor: buttonColor, opacity: 0.9 },
              }}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Location Settings"}
            </Button>
          </Box>
        </Grid>
      </form>

      {/* Caption Upload Modal for Custom Avatar Video */}
      <CaptionUploadModal
        isOpen={captionModalOpen}
        onClose={() => setCaptionModalOpen(false)}
        onUpload={handleCustomAvatarCaptionUpload}
        existingCaptionUrl={item?.avatarVideo?.captionFile}
        buttonColor={buttonColor}
        showMsgSuccessToast={showMsgSuccessToast}
        showErrorToast={showErrorToast}
      />
    </Box>
  );
};

export default WayfinderLocationSettings;
